import { ReactElement, useEffect, useRef, useState } from 'react'

import { ExclamationCircleFilled } from '@ant-design/icons'
import { message } from 'antd'
import { useTranslation } from 'react-i18next'

import { Flex } from 'components/core'
import { enterImpersonationMode } from 'features/Auth/authService'
import { userHasCustomerServicePermissions } from 'helpers/featurePermissions'
import { useAuthDomainValue, useUserHasPermissions } from 'hooks'

import * as styles from './styles.scss'

const HIDE_DURATION_MS = 60_000

export const ImpersonationModeNavItem = (): ReactElement | null => {
    const { t } = useTranslation('auth')

    const isImpersonationMode = useUserHasPermissions(
        userHasCustomerServicePermissions
    )
    const isImpersonating = useAuthDomainValue('userIsImpersonating')

    const [isHidden, setIsHidden] = useState<boolean>(false)
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

    useEffect(() => {
        return () => {
            clearTimeout(hideTimeoutRef.current)
        }
    }, [])

    if (!isImpersonationMode || !isImpersonating || isHidden) {
        return null
    }

    const handleExit = (): void => {
        // Rethrows after dispatching impersonationModeFailure;
        // swallow here to avoid an unhandled promise rejection.
        void enterImpersonationMode(false).catch(() => {})
    }

    const handleHide = (): void => {
        message.info({
            content: t(
                'auth:impersonationMode.hideToast',
                'Hiding impersonation mode tag for 60 seconds.'
            ),
            icon: (
                <ExclamationCircleFilled
                    className={styles.impersonationToastIcon}
                />
            ),
        })
        clearTimeout(hideTimeoutRef.current)
        setIsHidden(true)
        hideTimeoutRef.current = setTimeout(() => {
            setIsHidden(false)
        }, HIDE_DURATION_MS)
    }

    return (
        <>
            <div className={styles.impersonationTopLine} aria-hidden="true" />
            <Flex alignItems="center" className={styles.impersonationPill}>
                <span className={styles.impersonationPillLabel}>
                    {t('auth:impersonationMode.label', 'Impersonation Mode')}
                </span>
                <span className={styles.impersonationPillDivider} />
                <button
                    type="button"
                    className={styles.impersonationPillButton}
                    onClick={handleExit}
                    aria-label={t(
                        'auth:impersonationMode.exitAriaLabel',
                        'Exit impersonation mode'
                    )}
                >
                    {t('auth:impersonationMode.exit', 'Exit')}
                </button>
                <button
                    type="button"
                    className={styles.impersonationPillButton}
                    onClick={handleHide}
                    aria-label={t(
                        'auth:impersonationMode.hideAriaLabel',
                        'Hide impersonation mode tag for 60 seconds'
                    )}
                >
                    {t('auth:impersonationMode.hide', 'Hide')}
                </button>
            </Flex>
        </>
    )
}
